/** @file Auth facade over Firebase Auth with German error mapping. */
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { getAuth } from "./firebase.js";

/** @typedef {import("firebase/auth").User} User */

/** @type {Record<string, string>} */
const ERROR_MESSAGES = {
  "auth/invalid-email": "Ungültige E-Mail",
  "auth/user-not-found": "Kein Konto mit dieser E-Mail",
  "auth/wrong-password": "Passwort ist falsch",
  "auth/invalid-credential": "E-Mail oder Passwort ist falsch",
  "auth/email-already-in-use": "E-Mail ist bereits registriert",
  "auth/weak-password": "Passwort muss min. 6 Zeichen haben",
  "auth/too-many-requests": "Zu viele Versuche — bitte später erneut probieren",
  "auth/popup-blocked": "Pop-up blockiert — versuche es erneut",
  "auth/popup-closed-by-user": "Anmeldung abgebrochen",
  "auth/network-request-failed": "Netzwerkfehler — bitte Verbindung prüfen",
  "auth/requires-recent-login": "Bitte erneut anmelden, um fortzufahren",
};

/**
 * @param {unknown} err
 * @returns {string}
 */
export function mapAuthError(err) {
  const code =
    err && typeof err === "object" && "code" in err && typeof err.code === "string"
      ? err.code
      : "";
  return ERROR_MESSAGES[code] ?? "Ein unerwarteter Fehler ist aufgetreten";
}

/** @returns {import("firebase/auth").Auth} */
function auth() {
  const a = getAuth();
  if (!a) {
    throw new Error("Firebase Auth not initialized");
  }
  return a;
}

/** Ensures sessions persist across browser refreshes. */
export async function initAuthPersistence() {
  try {
    await setPersistence(auth(), browserLocalPersistence);
  } catch (err) {
    // Safari private mode / storage blocked — fall back to in-memory persistence silently.
    console.warn("[auth] setPersistence failed, using session default", err);
  }
}

/**
 * @param {string} email
 * @param {string} password
 * @returns {Promise<User>}
 */
export async function registerWithEmail(email, password) {
  const cred = await createUserWithEmailAndPassword(auth(), email, password);
  return cred.user;
}

/**
 * @param {string} email
 * @param {string} password
 * @returns {Promise<User>}
 */
export async function loginWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth(), email, password);
  return cred.user;
}

/** @returns {boolean} */
function isMobileViewport() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(pointer: coarse)").matches;
}

/** Google sign-in: popup on desktop, redirect on touch devices. */
export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  if (isMobileViewport()) {
    await signInWithRedirect(auth(), provider);
    return null;
  }
  const cred = await signInWithPopup(auth(), provider);
  return cred.user;
}

/**
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth(), email);
}

export async function logout() {
  await signOut(auth());
}

/**
 * @param {(user: User | null) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeAuth(fn) {
  return onAuthStateChanged(auth(), fn);
}
